import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { CheckCircle2, Clock } from "lucide-react";
import { apiClient } from "@/lib/api";
import { orderDiscountLabel } from "@/lib/discounts";
import { Button } from "@/components/ui/button";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const email = location.state?.email;
    apiClient.get(`/orders/${orderNumber}`, { params: email ? { email } : {} })
      .then((res) => setOrder(res.data))
      .catch(() => setError("We couldn't locate that order."));
  }, [orderNumber, location.state]);

  if (error) return <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="order-confirmation-error">{error}</div>;
  if (!order) return <div className="max-w-xl mx-auto px-6 py-24" data-testid="order-confirmation-loading"><div className="h-64 bg-slate-100 rounded-2xl animate-pulse" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-6 py-20" data-testid="order-confirmation-page">
      <div className="text-center mb-10">
        <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" strokeWidth={1.5} />
        <h1 className="text-3xl font-display font-medium text-slate-900">Order Received</h1>
        <p className="text-sm text-slate-500 mt-2">Thank you, {order.shipping_address.full_name}. Your order has been recorded.</p>
      </div>

      <div className="border border-slate-200 rounded-2xl p-6 space-y-4">
        <div className="flex justify-between text-sm"><span className="text-slate-500">Order Number</span><span className="font-semibold text-slate-900" data-testid="confirmation-order-number">{order.order_number}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-500">Payment Status</span>
          {order.payment_status === "paid" ? (
            <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600" data-testid="confirmation-payment-status"><CheckCircle2 className="h-3.5 w-3.5" /> Paid</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 font-semibold text-amber-600" data-testid="confirmation-payment-status"><Clock className="h-3.5 w-3.5" /> Pending</span>
          )}
        </div>
        <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-semibold text-slate-900">${order.subtotal.toFixed(2)}</span></div>
        {order.discount > 0 && (
          <div className="flex justify-between text-sm text-emerald-600" data-testid="confirmation-discount">
            <span>{orderDiscountLabel(order)}</span><span>-${order.discount.toFixed(2)}</span>
          </div>
        )}
        {order.shipping_cost > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Shipping</span><span className="font-semibold text-slate-900">${order.shipping_cost.toFixed(2)}</span></div>}
        <div className="flex justify-between text-sm"><span className="text-slate-500">Total</span><span className="font-semibold text-slate-900">${order.total.toFixed(2)}</span></div>

        <div className="pt-4 border-t border-slate-100 space-y-2">
          {order.items.map((item) => (
            <div key={item.product_id} className="flex justify-between text-sm text-slate-600">
              <span>{item.name} x{item.quantity}</span><span>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>

        {order.payment_status !== "paid" && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800 leading-relaxed" data-testid="confirmation-payment-notice">
            Thank you for your order! You will receive an email with payment instructions to move forward. Please include your order number <span className="font-bold">{order.order_number}</span> as the payment memo.
          </div>
        )}
      </div>

      <div className="text-center mt-8">
        <Link to="/shop"><Button variant="outline" className="rounded-full" data-testid="confirmation-continue-shopping-btn">Continue Shopping</Button></Link>
      </div>
    </div>
  );
}
